import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useApi } from "../api/context";
import { qk } from "../api/queries";
import { useAuth } from "../auth/AuthContext";
import { refreshCatalogue } from "./catalogueCache";
import { refreshQueueCounts, setQueueBranch } from "./saleQueue";
import { runSync } from "./sync";
import { registerSyncRunner, syncNow } from "./syncState";

const SYNC_EVERY_MS = 30_000;
const CATALOGUE_EVERY_MS = 10 * 60_000;

/** Background offline duties for a signed-in tenant user (§16.1): sync on load, when the
 * browser comes back online and every 30 s; keep the offline catalogue fresh. */
export function SyncAgent() {
  const { api } = useApi();
  const { user } = useAuth();
  const qc = useQueryClient();
  const branchId = user?.branchId ?? null;
  const tenantId = user?.tenantId ?? null;
  const ref = useRef({ api, branchId });
  useEffect(() => {
    ref.current = { api, branchId };
    setQueueBranch(branchId);
    void refreshQueueCounts();
  }, [api, branchId]);

  useEffect(() => {
    registerSyncRunner(async () => {
      await runSync(ref.current.api, ref.current.branchId, () => {
        for (const k of [["sales"], qk.tillCurrent, qk.products, qk.customers, qk.inventory, ["reports"]]) void qc.invalidateQueries({ queryKey: k });
      });
    });
    void refreshQueueCounts();
    const kick = () => {
      if (navigator.onLine !== false) void syncNow();
    };
    kick();
    window.addEventListener("online", kick);
    const t = setInterval(kick, SYNC_EVERY_MS);
    return () => {
      window.removeEventListener("online", kick);
      clearInterval(t);
      registerSyncRunner(async () => undefined);
    };
  }, [qc]);

  useEffect(() => {
    if (tenantId === null) return;
    const refresh = () => {
      if (navigator.onLine !== false) void refreshCatalogue(api, tenantId);
    };
    refresh();
    const t = setInterval(refresh, CATALOGUE_EVERY_MS);
    window.addEventListener("online", refresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("online", refresh);
    };
  }, [api, tenantId]);

  return null;
}
